/** @jsx jsx */
import { jsx } from '@emotion/core';
import { FC } from 'react';
import { graphql } from 'gatsby';

import { Box, Flex, Heading, Text, BoxProps } from 'rebass';
import Card, { CardProps } from './card';

import MainContainer from '../../../ui-components/container/container';
import { FlexProps } from 'styled-system';

export interface ICardsProps {
  cards?: CardProps[];
  spacer?: number;
  cardsPerRow?: number;
  secondaryBg?: boolean;
}

export interface CardsSectionProps {
  className?: string;
  id?: string;
  spacer?: number;

  data?: {
    header?: string;
    subheader?: {
      subheader?: string;
    };
    layout?: any;
    secondaryBg?: boolean;
    cardsPerRow?: number;
    cards?: CardProps[];
  };
}

export const query = graphql`
  fragment Card on ContentfulCard {
    id
    title
    paragraph {
      paragraph
    }
    link {
      ...Links
    }
    primaryImage {
      ...ContentfulAsset
    }
    secondaryImage {
      ...ContentfulAsset
    }
  }
`;

export const cardsQuery = graphql`
  fragment CardsSection on ContentfulCardsSection {
    id
    header
    subheader {
      subheader
    }
    cardsPerRow
    secondaryBg
    cards {
      ...Card
    }
    layout
    internal {
      type
    }
  }
`;

export const Cards: FC<ICardsProps &
  FlexProps &
  BoxProps &
  JSX.IntrinsicElements['div']> = ({
  cards,
  spacer = 10,
  cardsPerRow = 4,
  secondaryBg = false,
  layout = 'Rows',
  ...rest
}) => {
  return (
    <Flex
      width="100%"
      flexWrap="wrap"
      margin={`-${spacer}px -${spacer}px`}
      {...rest}
    >
      {cards.map(
        ({
          node: {
            id,
            title,
            name,
            gradeLevel,
            experienceQuarter,
            experienceYear,
            lessonNumber,
            summary,
            cardUrl,
            image
          }
        }) => {
          return (
            <Card
              key={id || title}
              name={name}
              image={image}
              secondaryHeader={true}
              gradeLevel={gradeLevel}
              experienceQuarter={experienceQuarter}
              experienceYear={experienceYear}
              lessonNumber={lessonNumber}
              summary={summary}
              cardUrl={cardUrl}
              width={[
                `calc(100% - ${spacer * 4}px)`,
                `calc((100% * 1/2) - ${spacer * 4}px)`,
                `calc((100% * 1/${cardsPerRow}) - ${spacer * 4}px)`
              ]}
              m={`${spacer * 2}px`}
            />
          )}
      )}
    </Flex>
  );
};

const CardsSection: FC<CardsSectionProps & JSX.IntrinsicElements['div']> = ({
  className = '',
  id = '',
  spacer = 10, // Spacer in pixels
  data: { header, subheader, cardsPerRow = 4, secondaryBg = false, cards } = {},
  ...rest
}) => {
  return (
    <Box py={4}>
      <MainContainer>
        {header && (
          <Heading
            variant="heading"
            mb={subheader ? '' : 4}
            flex="0 1 100%"
            maxWidth="100%"
          >
            {header}
          </Heading>
        )}
        {subheader && subheader.subheader && (
          <Text mb={4} variant="" flex="0 1 100%" maxWidth="100%">
            {subheader.subheader}
          </Text>
        )}
        {cards && cards.length > 0 && (
          <Cards
            cards={cards}
            cardsPerRow={cardsPerRow}
            secondaryBg={secondaryBg}
            spacer={spacer}
          />
        )}
      </MainContainer>
    </Box>
  );
};

export default CardsSection;
